import { formatBytes, formatEta, type UploadProgress } from "./UploadEngine";

/**
 * Editorial progress bar for the YouTube upload page. A single thin violet
 * bar with tabular bytes/total and italic ETA. Linear (not eased) so the
 * motion reads as truth, not theater.
 */
export function UploadProgressBar({
  progress,
  fileName,
  fileSize,
  status,
}: {
  progress: UploadProgress | null;
  fileName: string;
  fileSize: number;
  status: "idle" | "uploading" | "paused" | "complete" | "error";
}) {
  const ratio = progress?.ratio ?? 0;
  const pct = Math.round(ratio * 100);
  const bytesUploaded = progress?.bytesUploaded ?? 0;
  const speed = progress?.speedBps ?? 0;

  return (
    <div className="yt-up-progress" data-status={status}>
      <div className="yt-up-progress-meta">
        <span className="yt-up-progress-name">{fileName}</span>
        <span className="yt-up-progress-bytes tabular">
          {formatBytes(bytesUploaded)} / {formatBytes(fileSize)}
        </span>
      </div>
      <div className="yt-up-progress-bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>
        <div
          className="yt-up-progress-fill"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="yt-up-progress-foot">
        <span className="yt-up-progress-pct tabular">
          {pct}%
        </span>
        {status === "uploading" && progress && (
          <>
            <span className="yt-up-progress-sep">·</span>
            <span className="font-display-italic">
              {formatBytes(speed)}/s
            </span>
            <span className="yt-up-progress-sep">·</span>
            <span className="font-display-italic">
              {formatEta(progress.etaSec)} remaining
            </span>
          </>
        )}
        {status === "paused" && (
          <>
            <span className="yt-up-progress-sep">·</span>
            <span className="font-display-italic text-[var(--color-violet-deep)]">
              Reconnecting…
            </span>
          </>
        )}
        {status === "complete" && (
          <>
            <span className="yt-up-progress-sep">·</span>
            <span className="font-display-italic">Uploaded</span>
          </>
        )}
        {status === "error" && (
          <>
            <span className="yt-up-progress-sep">·</span>
            <span className="font-display-italic text-[var(--color-violet-deep)]">
              Failed
            </span>
          </>
        )}
      </div>
    </div>
  );
}
